import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import {
  Eye,
  TrendingUp,
  Calendar,
  AlertCircle,
  BarChart3,
  // Facebook, // META TEMPORARILY DISABLED
  // Instagram, // META TEMPORARILY DISABLED
  Settings,
  RefreshCw,
  UserPlus,
  Share2,
  Filter,
  Check,
  Link2,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  MapPin,
  Clock,
  MousePointerClick,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  LayoutGrid,
  GitCompareArrows,
  ChevronRight,
} from "lucide-react";
// import MetaAPIService from "../../api/meta"; // META TEMPORARILY DISABLED
import GoogleAnalyticsService from "../../api/googleAnalytics";
import SocialMediaConnect from "./SocialMediaConnect";
import {
  Tooltip as RadixTooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../../components/ui/tooltip";

// ─── Setup Guide Component ─────────────────────────────────────────────────
const SetupGuide = ({ isDark, isAdmin = true }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const scriptSnippet = `<!-- Google tag (gtag.js) -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(scriptSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between p-4 text-left transition-colors ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50"}`}
      >
        <div className="flex items-center gap-2.5">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? "bg-indigo-500/15" : "bg-indigo-50"}`}
          >
            <HelpCircle
              className={`h-4 w-4 ${isDark ? "text-indigo-400" : "text-indigo-600"}`}
            />
          </div>
          <div>
            <span
              className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              {isAdmin
                ? "Hoe verbind ik mijn website?"
                : "Google Analytics instellen"}
            </span>
            <p
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              {isAdmin
                ? "Stap-voor-stap uitleg om Google Analytics te koppelen"
                : "Stuur deze info door naar je websitebouwer"}
            </p>
          </div>
        </div>
        {open ? (
          <ChevronUp
            className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          />
        ) : (
          <ChevronDown
            className={`h-4 w-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          />
        )}
      </button>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.2 }}
          className={`border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <div className="p-4 space-y-4">
            {isAdmin ? (
              <>
                {/* Admin: full step-by-step guide */}
                {/* Step 1 */}
                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-indigo-500/20 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    1
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Maak een Google Analytics account aan
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Ga naar{" "}
                      <a
                        href="https://analytics.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`underline ${isDark ? "text-indigo-400" : "text-indigo-600"}`}
                      >
                        analytics.google.com
                      </a>{" "}
                      en log in met hetzelfde Google account dat u hier hebt
                      verbonden.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-indigo-500/20 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    2
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Maak een property aan
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Klik op <strong>Admin</strong> (tandwiel links onderaan) →{" "}
                      <strong>Create Property</strong>. Geef het de naam van uw
                      website.
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-indigo-500/20 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    3
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Voeg een webdatastream toe
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Kies <strong>Web</strong> als platform, voer uw website
                      URL in (bv.{" "}
                      <code
                        className={`text-[11px] px-1 py-0.5 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                      >
                        https://uwwebsite.be
                      </code>
                      ) en geef de stream een naam.
                    </p>
                  </div>
                </div>

                {/* Step 4 */}
                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-indigo-500/20 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    4
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Kopieer de tracking code
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 mb-2 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      U ontvangt een <strong>Measurement ID</strong> (
                      <code
                        className={`text-[11px] px-1 py-0.5 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                      >
                        G-XXXXXXXXXX
                      </code>
                      ). Plaats onderstaande code in de{" "}
                      <code
                        className={`text-[11px] px-1 py-0.5 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                      >
                        &lt;head&gt;
                      </code>{" "}
                      van uw website. Vervang{" "}
                      <code
                        className={`text-[11px] px-1 py-0.5 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                      >
                        G-XXXXXXXXXX
                      </code>{" "}
                      door uw eigen ID.
                    </p>
                    <div className="relative">
                      <pre
                        className={`text-[11px] leading-relaxed p-3 rounded-lg overflow-x-auto ${isDark ? "bg-black/40 text-gray-400 border border-white/[0.06]" : "bg-gray-50 text-gray-600 border border-gray-200"}`}
                      >
                        {scriptSnippet}
                      </pre>
                      <button
                        onClick={handleCopy}
                        className={`absolute top-2 right-2 p-1.5 rounded-md transition-colors ${isDark ? "hover:bg-white/[0.08] text-gray-500" : "hover:bg-gray-200 text-gray-400"}`}
                        title="Kopieer code"
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}
                  >
                    ✓
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Klaar!
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Zodra uw website verkeer ontvangt, verschijnen de
                      statistieken automatisch in dit dashboard. Het kan tot 24
                      uur duren voordat de eerste data zichtbaar is.
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Non-admin: simplified guide */}
                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-indigo-500/20 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    1
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Stuur de tracking code door naar je websitebouwer
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Vraag je beheerder om de Google Analytics Measurement ID (
                      <code
                        className={`text-[11px] px-1 py-0.5 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                      >
                        G-XXXXXXXXXX
                      </code>
                      ). Stuur deze code door naar je websitebouwer zodat die
                      het in de website kan plaatsen.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-indigo-500/20 text-indigo-400" : "bg-indigo-100 text-indigo-700"}`}
                  >
                    2
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      De code die je websitebouwer nodig heeft
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 mb-2 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Kopieer onderstaande code en stuur deze door. Je
                      websitebouwer plaatst dit in de{" "}
                      <code
                        className={`text-[11px] px-1 py-0.5 rounded ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                      >
                        &lt;head&gt;
                      </code>{" "}
                      van je website.
                    </p>
                    <div className="relative">
                      <pre
                        className={`text-[11px] leading-relaxed p-3 rounded-lg overflow-x-auto ${isDark ? "bg-black/40 text-gray-400 border border-white/[0.06]" : "bg-gray-50 text-gray-600 border border-gray-200"}`}
                      >
                        {scriptSnippet}
                      </pre>
                      <button
                        onClick={handleCopy}
                        className={`absolute top-2 right-2 p-1.5 rounded-md transition-colors ${isDark ? "hover:bg-white/[0.08] text-gray-500" : "hover:bg-gray-200 text-gray-400"}`}
                        title="Kopieer code"
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isDark ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-100 text-emerald-700"}`}
                  >
                    ✓
                  </div>
                  <div>
                    <p
                      className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-800"}`}
                    >
                      Klaar!
                    </p>
                    <p
                      className={`text-[12px] mt-0.5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
                    >
                      Zodra je websitebouwer de code heeft geplaatst en je
                      website verkeer ontvangt, verschijnen de statistieken
                      automatisch hier. Het kan tot 24 uur duren.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// ─── Skeleton Loader Component ──────────────────────────────────────────────
const StatisticsSkeleton = ({ isDark }) => {
  const pulseClass = isDark
    ? "animate-pulse bg-white/[0.04]"
    : "animate-pulse bg-gray-200/70";
  const cardClass = isDark
    ? "bg-white/[0.03] border border-white/[0.06]"
    : "bg-white border border-gray-200/80";

  return (
    <div className="space-y-5">
      {/* Stat cards skeleton */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`rounded-xl p-4 ${cardClass}`}>
            <div className="flex items-center gap-1.5 mb-3">
              <div className={`h-3.5 w-3.5 rounded ${pulseClass}`} />
              <div className={`h-3 w-16 rounded ${pulseClass}`} />
            </div>
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${pulseClass}`} />
              <div className={`h-6 w-14 rounded ${pulseClass}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Header skeleton */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className={`h-5 w-28 rounded ${pulseClass}`} />
        <div className="flex items-center gap-2">
          <div className={`h-6 w-16 rounded-full ${pulseClass}`} />
          <div className={`h-9 w-[160px] rounded-lg ${pulseClass}`} />
          <div className={`h-9 w-9 rounded-lg ${pulseClass}`} />
          <div className={`h-9 w-9 rounded-lg ${pulseClass}`} />
        </div>
      </div>

      {/* Chart skeleton */}
      <div className={`rounded-xl p-4 ${cardClass}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className={`h-4 w-4 rounded ${pulseClass}`} />
          <div className={`h-4 w-32 rounded ${pulseClass}`} />
        </div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className={`h-4 w-36 rounded mb-1.5 ${pulseClass}`} />
            <div className={`h-3 w-24 rounded ${pulseClass}`} />
          </div>
        </div>
        <div className="h-64 flex items-end gap-1 px-4">
          {Array.from({ length: 14 }).map((_, i) => (
            <div
              key={i}
              className={`flex-1 rounded-t ${pulseClass}`}
              style={{
                height: `${20 + Math.random() * 70}%`,
                animationDelay: `${i * 75}ms`,
              }}
            />
          ))}
        </div>
      </div>

      {/* Bottom sections skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className={`rounded-xl p-4 ${cardClass}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`h-3.5 w-3.5 rounded ${pulseClass}`} />
              <div className={`h-4 w-28 rounded ${pulseClass}`} />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j}>
                  <div className="flex justify-between mb-1.5">
                    <div className={`h-3.5 w-24 rounded ${pulseClass}`} />
                    <div className={`h-3.5 w-16 rounded ${pulseClass}`} />
                  </div>
                  <div className={`h-1.5 rounded-full ${pulseClass}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Loading indicator text */}
      <div className="flex items-center justify-center gap-2 py-2">
        <div
          className={`w-4 h-4 rounded-full border-2 animate-spin ${isDark
            ? "border-white/[0.08] border-t-white/40"
            : "border-gray-200 border-t-gray-600"
            }`}
        />
        <span
          className={`text-[12px] font-medium ${isDark ? "text-gray-500" : "text-gray-400"
            }`}
        >
          Statistieken laden…
        </span>
      </div>
    </div>
  );
};

const Statistics = () => {
  const { company, isAdmin } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [loading, setLoading] = useState(true);
  const [propertiesLoading, setPropertiesLoading] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [timeframe, setTimeframe] = useState("week");
  const [showConnections, setShowConnections] = useState(false);
  const [showSetupGuide, setShowSetupGuide] = useState(false);
  const [connections, setConnections] = useState({
    // meta: { connected: false, data: null }, // META TEMPORARILY DISABLED
    google: { connected: false, data: null },
  });
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [gaProperties, setGaProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [viewMode, setViewMode] = useState("normal"); // "normal" | "comparison"

  // Toggle a property on/off for comparison
  const toggleProperty = (propertyId) => {
    setSelectedProperties((prev) => {
      if (prev.includes(propertyId)) {
        if (prev.length === 1) return prev; // keep at least one
        return prev.filter((id) => id !== propertyId);
      }
      return [...prev, propertyId];
    });
  };

  // Select single property in normal mode
  const selectSingleProperty = (propertyId) => {
    setSelectedProperties([propertyId]);
  };

  // Property color palette for multi-property comparison
  const propertyColorPalette = [
    {
      main: isDark ? "#818cf8" : "#4f46e5",
      bg: isDark ? "bg-indigo-500/15" : "bg-indigo-50",
      border: isDark ? "border-indigo-500/30" : "border-indigo-200",
      text: isDark ? "text-indigo-400" : "text-indigo-700",
      dot: isDark ? "bg-indigo-400" : "bg-indigo-500",
    },
    {
      main: isDark ? "#34d399" : "#10b981",
      bg: isDark ? "bg-emerald-500/15" : "bg-emerald-50",
      border: isDark ? "border-emerald-500/30" : "border-emerald-200",
      text: isDark ? "text-emerald-400" : "text-emerald-700",
      dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
    },
    {
      main: isDark ? "#fbbf24" : "#f59e0b",
      bg: isDark ? "bg-amber-500/15" : "bg-amber-50",
      border: isDark ? "border-amber-500/30" : "border-amber-200",
      text: isDark ? "text-amber-400" : "text-amber-700",
      dot: isDark ? "bg-amber-400" : "bg-amber-500",
    },
    {
      main: isDark ? "#f472b6" : "#ec4899",
      bg: isDark ? "bg-pink-500/15" : "bg-pink-50",
      border: isDark ? "border-pink-500/30" : "border-pink-200",
      text: isDark ? "text-pink-400" : "text-pink-700",
      dot: isDark ? "bg-pink-400" : "bg-pink-500",
    },
    {
      main: isDark ? "#38bdf8" : "#0ea5e9",
      bg: isDark ? "bg-sky-500/15" : "bg-sky-50",
      border: isDark ? "border-sky-500/30" : "border-sky-200",
      text: isDark ? "text-sky-400" : "text-sky-700",
      dot: isDark ? "bg-sky-400" : "bg-sky-500",
    },
  ];

  const getPropertyColor = (propertyId) => {
    const idx = gaProperties.findIndex(
      (p) => p.name.split("/").pop() === propertyId,
    );
    return propertyColorPalette[
      idx >= 0 ? idx % propertyColorPalette.length : 0
    ];
  };

  const getPropertyName = (propertyId) => {
    const prop = gaProperties.find(
      (p) => p.name.split("/").pop() === propertyId,
    );
    return prop?.displayName || propertyId;
  };

  useEffect(() => {
    checkConnections();
  }, []);

  // Fetch GA properties when Google gets connected
  useEffect(() => {
    if (connections.google.connected) {
      fetchGAProperties();
    }
  }, [connections.google.connected]);

  useEffect(() => {
    if (/* connections.meta.connected || */ connections.google.connected) {
      fetchStats();
    } else {
      setStats(null);
      setLoading(false);
    }
  }, [timeframe, connections, selectedPlatform, selectedProperties]);

  const fetchGAProperties = async () => {
    setPropertiesLoading(true);
    try {
      const googleToken = localStorage.getItem("google_analytics_token");
      if (!googleToken) {
        setPropertiesLoading(false);
        return;
      }
      const accounts = await GoogleAnalyticsService.getAccounts(googleToken);
      if (accounts.length > 0) {
        const allProperties = [];
        for (const account of accounts) {
          const props = await GoogleAnalyticsService.getProperties(
            account.name,
            googleToken,
          );
          allProperties.push(
            ...props.map((p) => ({
              ...p,
              accountName: account.displayName || account.name,
            })),
          );
        }
        setGaProperties(allProperties);
        if (allProperties.length > 0 && selectedProperties.length === 0) {
          // In normal mode, select first property; in comparison mode, select all
          if (viewMode === "comparison") {
            setSelectedProperties(
              allProperties.map((p) => p.name.split("/").pop()),
            );
          } else {
            setSelectedProperties([allProperties[0].name.split("/").pop()]);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching GA properties:", err);
    } finally {
      setPropertiesLoading(false);
    }
  };

  const checkConnections = async () => {
    try {
      /* META TEMPORARILY DISABLED
      const metaStatus = await MetaAPIService.checkLoginStatus();
      if (metaStatus.status === "connected") {
        setConnections((prev) => ({
          ...prev,
          meta: { connected: true, data: null },
        }));
      }
      */

      let googleToken = localStorage.getItem("google_analytics_token");
      if (googleToken) {
        try {
          let isValid = await GoogleAnalyticsService.validateToken(googleToken);

          if (!isValid) {
            const refreshToken = localStorage.getItem("google_analytics_refresh_token");
            if (refreshToken) {
              try {
                const refreshed = await GoogleAnalyticsService.refreshToken(refreshToken);
                if (refreshed?.access_token) {
                  localStorage.setItem("google_analytics_token", refreshed.access_token);
                  googleToken = refreshed.access_token;
                  isValid = true;
                }
              } catch (refreshError) {
                console.log("Google Analytics token refresh failed:", refreshError);
              }
            }
          }

          if (isValid) {
            setConnections((prev) => ({
              ...prev,
              google: { connected: true, data: null },
            }));
          }
        } catch (googleError) {
          console.log("Google Analytics token validation failed:", googleError);
        }
      }
    } catch (error) {
      console.error("Error checking connections:", error);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    setError("");

    try {
      const allStats = {
        facebookPages: [],
        instagramAccounts: [],
        totalFollowers: 0,
        totalPosts: 0,
        totalEngagement: 0,
        totalImpressions: 0,
        totalReach: 0,
        pageInsights: [],
        hasData: false,
      };

      // Fetch Meta data - META TEMPORARILY DISABLED
      /* if (
        connections.meta.connected &&
        (selectedPlatform === "all" || selectedPlatform === "meta")
      ) {
        const metaStatus = await MetaAPIService.checkLoginStatus();
        if (metaStatus.status === "connected") {
          const pages = await MetaAPIService.getPages(
            metaStatus.authResponse.accessToken,
          );

          for (const page of pages) {
            const pageData = {
              id: page.id,
              name: page.name,
              followers: 0,
              posts: 0,
              engagement: 0,
              impressions: 0,
              reach: 0,
              insights: [],
            };

            try {
              const fbInsights = await MetaAPIService.getFacebookPageInsights(
                page.id,
                page.access_token,
              );
              if (fbInsights && fbInsights.length > 0) {
                pageData.insights = fbInsights;
                pageData.impressions =
                  fbInsights.find((i) => i.name === "page_impressions")
                    ?.values?.[0]?.value || 0;
                allStats.totalImpressions += pageData.impressions;
              }
            } catch (e) {
              console.log("Could not fetch FB insights:", e.message);
            }

            try {
              const fbPosts = await MetaAPIService.getFacebookPosts(
                page.id,
                page.access_token,
                100,
              );
              if (fbPosts && fbPosts.length > 0) {
                pageData.posts = fbPosts.length;
                allStats.totalPosts += pageData.posts;
              }
            } catch (e) {
              console.log("Could not fetch FB posts:", e.message);
            }

            try {
              const instagramAccount =
                await MetaAPIService.getInstagramAccounts(
                  page.id,
                  page.access_token,
                );
              if (instagramAccount) {
                const instagramData = {
                  id: instagramAccount.id,
                  username: instagramAccount.username,
                  pageId: page.id,
                  pageName: page.name,
                  followers: 0,
                  posts: 0,
                  engagement: 0,
                  impressions: 0,
                  reach: 0,
                };

                const insights = await MetaAPIService.getInstagramInsights(
                  instagramAccount.id,
                  page.access_token,
                  ["impressions", "reach", "profile_views", "follower_count"],
                );

                if (insights && insights.length > 0) {
                  instagramData.followers =
                    insights.find((i) => i.name === "follower_count")
                      ?.values?.[0]?.value || 0;
                  instagramData.impressions =
                    insights.find((i) => i.name === "impressions")?.values?.[0]
                      ?.value || 0;
                  instagramData.reach =
                    insights.find((i) => i.name === "reach")?.values?.[0]
                      ?.value || 0;
                  allStats.totalFollowers += instagramData.followers;
                  allStats.totalImpressions += instagramData.impressions;
                  allStats.totalReach += instagramData.reach;
                }

                const posts = await MetaAPIService.getInstagramPosts(
                  instagramAccount.id,
                  page.access_token,
                  100,
                );

                if (posts && posts.length > 0) {
                  instagramData.posts = posts.length;
                  instagramData.engagement = posts.reduce(
                    (sum, post) =>
                      sum + (post.like_count || 0) + (post.comments_count || 0),
                    0,
                  );
                  allStats.totalPosts += instagramData.posts;
                  allStats.totalEngagement += instagramData.engagement;
                }

                allStats.instagramAccounts.push(instagramData);
              }
            } catch (e) {
              console.log("Could not fetch Instagram data:", e.message);
            }

            allStats.facebookPages.push(pageData);
          }
        }
      }
      */

      // Fetch Google Analytics data for all selected properties
      if (
        connections.google.connected &&
        (selectedPlatform === "all" || selectedPlatform === "google")
      ) {
        const googleToken = localStorage.getItem("google_analytics_token");
        if (googleToken && selectedProperties.length > 0) {
          const dateParamMap = {
            day: "1daysAgo",
            week: "7daysAgo",
            month: "30daysAgo",
            "90days": "90daysAgo",
            "6months": "180daysAgo",
            year: "365daysAgo",
          };
          const dateParam = dateParamMap[timeframe] || "7daysAgo";

          // Helper to fetch & process data for a single property
          const fetchPropertyData = async (propertyId) => {
            const [
              analyticsResult,
              pageResult,
              trafficResult,
              deviceResult,
              countryResult,
            ] = await Promise.allSettled([
              GoogleAnalyticsService.getAnalyticsData(
                propertyId,
                googleToken,
                dateParam,
              ),
              GoogleAnalyticsService.getPageAnalytics(
                propertyId,
                googleToken,
                dateParam,
              ),
              GoogleAnalyticsService.getTrafficSources(
                propertyId,
                googleToken,
                dateParam,
              ),
              GoogleAnalyticsService.getDeviceBreakdown(
                propertyId,
                googleToken,
                dateParam,
              ),
              GoogleAnalyticsService.getCountryBreakdown(
                propertyId,
                googleToken,
                dateParam,
              ),
            ]);

            const analyticsData =
              analyticsResult.status === "fulfilled"
                ? analyticsResult.value
                : [];
            const pageData =
              pageResult.status === "fulfilled" ? pageResult.value : [];
            const trafficData =
              trafficResult.status === "fulfilled" ? trafficResult.value : [];
            const deviceData =
              deviceResult.status === "fulfilled" ? deviceResult.value : [];
            const countryData =
              countryResult.status === "fulfilled" ? countryResult.value : [];

            const totalSessions = analyticsData.reduce(
              (sum, item) => sum + (item.sessions || 0),
              0,
            );
            const totalUsers = analyticsData.reduce(
              (sum, item) => sum + (item.totalUsers || 0),
              0,
            );
            const totalPageViews = analyticsData.reduce(
              (sum, item) => sum + (item.screenPageViews || 0),
              0,
            );
            const totalEngagedSessions = analyticsData.reduce(
              (sum, item) => sum + (item.engagedSessions || 0),
              0,
            );

            const weightedBounceRate =
              totalSessions > 0
                ? analyticsData.reduce(
                  (sum, item) =>
                    sum + (item.bounceRate || 0) * (item.sessions || 0),
                  0,
                ) / totalSessions
                : 0;
            const weightedAvgDuration =
              totalSessions > 0
                ? analyticsData.reduce(
                  (sum, item) =>
                    sum +
                    (item.averageSessionDuration || 0) * (item.sessions || 0),
                  0,
                ) / totalSessions
                : 0;

            return {
              visitorsByDay: analyticsData.map((item) => ({
                date: item.date,
                visitors: item.sessions || 0,
                uniqueVisitors: item.totalUsers || 0,
                pageViews: item.screenPageViews || 0,
                bounceRate: item.bounceRate || 0,
                avgDuration: item.averageSessionDuration || 0,
              })),
              totalVisitors: totalSessions,
              uniqueVisitors: totalUsers,
              totalPageViews,
              bounceRate: weightedBounceRate,
              avgSessionDuration: weightedAvgDuration,
              engagedSessions: totalEngagedSessions,
              engagementRate:
                totalSessions > 0
                  ? (totalEngagedSessions / totalSessions) * 100
                  : 0,
              popularPages: pageData.map((page) => ({
                page: page.page,
                title: page.title,
                views: page.pageViews,
                clicks: page.sessions,
                bounceRate: page.bounceRate,
              })),
              trafficSources: trafficData,
              deviceBreakdown: deviceData,
              countryBreakdown: countryData,
            };
          };

          // Fetch ALL selected properties in parallel
          const results = await Promise.allSettled(
            selectedProperties.map(async (pid) => ({
              propertyId: pid,
              data: await fetchPropertyData(pid),
            })),
          );

          allStats.propertiesData = {};
          results.forEach((result) => {
            if (result.status === "fulfilled") {
              allStats.propertiesData[result.value.propertyId] =
                result.value.data;
            }
          });

          // Set googleAnalytics to first selected property (backward compat)
          const firstKey = selectedProperties.find(
            (pid) => allStats.propertiesData[pid],
          );
          if (firstKey) {
            allStats.googleAnalytics = allStats.propertiesData[firstKey];
          }
        }
      }

      // Show stats if any connection is active (even with 0 data)
      allStats.hasData =
        connections.google.connected ||
        // connections.meta.connected || // META TEMPORARILY DISABLED
        allStats.totalFollowers > 0 ||
        allStats.totalPosts > 0 ||
        allStats.totalImpressions > 0 ||
        allStats.totalReach > 0 ||
        (allStats.googleAnalytics &&
          allStats.googleAnalytics.totalVisitors > 0);

      // Always initialize googleAnalytics & propertiesData if Google is connected
      if (connections.google.connected && !allStats.googleAnalytics) {
        const emptyGA = {
          visitorsByDay: [],
          totalVisitors: 0,
          uniqueVisitors: 0,
          totalPageViews: 0,
          bounceRate: 0,
          avgSessionDuration: 0,
          engagedSessions: 0,
          engagementRate: 0,
          popularPages: [],
          trafficSources: [],
          deviceBreakdown: [],
          countryBreakdown: [],
        };
        allStats.googleAnalytics = emptyGA;
        if (!allStats.propertiesData) allStats.propertiesData = {};
      }

      setStats(allStats);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching statistics:", err);
      setError(
        "Er is een fout opgetreden bij het ophalen van uw statistieken.",
      );
      setStats(null);
      setLoading(false);
    }
  };

  if (loading || propertiesLoading) {
    return <StatisticsSkeleton isDark={isDark} />;
  }

  if (error) {
    return (
      <div
        className={`flex items-center gap-2 px-4 py-3 rounded-xl text-[13px] ${isDark ? "bg-red-500/10 border border-red-500/20 text-red-400" : "bg-red-50 border border-red-200 text-red-700"}`}
      >
        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  // Show connection setup voor alle gebruikers als er geen Google-account is gekoppeld
  if (!connections.google.connected) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Statistieken
          </h1>
        </div>

        <div
          className={`rounded-xl p-8 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div
            className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <Link2
              className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
          <h3
            className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
          >
            {isAdmin ? "Verbind uw accounts" : "Google Analytics koppelen"}
          </h3>
          <p
            className={`text-[13px] mb-5 max-w-md mx-auto ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            {isAdmin
              ? "Verbind uw Google Analytics account om statistieken van uw website te bekijken."
              : "Koppel een Google Analytics account om statistieken te zien, of vraag je beheerder om dit te doen."}
          </p>
          {/* Hier kan een knop of formulier komen om Google Analytics te koppelen, zichtbaar voor alle gebruikers */}
        </div>

        <SetupGuide isDark={isDark} isAdmin={isAdmin} />
          <>
            <SocialMediaConnect
              onConnect={(platform) => {
                setConnections((prev) => ({
                  ...prev,
                  [platform]: { connected: true, data: null },
                }));
              }}
            />
          </>
      </div>
    );
  }

  // Show message if no data available
  if (!stats || !stats.hasData) {
    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1
            className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Statistieken
          </h1>
          <button
            onClick={fetchStats}
            className={`h-9 px-4 rounded-lg text-[13px] font-medium inline-flex items-center gap-2 transition-colors ${isDark ? "border border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Opnieuw proberen
          </button>
        </div>

        <div
          className={`rounded-xl p-12 text-center ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div
            className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
          >
            <BarChart3
              className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-400"}`}
            />
          </div>
          <h3
            className={`text-[14px] font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-900"}`}
          >
            Geen data beschikbaar
          </h3>
          <p
            className={`text-[13px] mb-2 max-w-md mx-auto ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            Er zijn momenteel geen statistieken beschikbaar. Dit kan komen door:
          </p>
          <ul
            className={`text-[12px] space-y-1 mb-5 ${isDark ? "text-gray-600" : "text-gray-500"}`}
          >
            <li>• Google Analytics tracking code nog niet geïnstalleerd</li>
            <li>• Onvoldoende data voor de geselecteerde periode</li>
            <li>• Het kan 24-48 uur duren voordat data binnenkomt</li>
          </ul>
        </div>

        <SetupGuide isDark={isDark} isAdmin={isAdmin} />
      </div>
    );
  }

  // Build stat cards dynamically — always show Google cards when connected
  const statCards = [];

  // Helper to format seconds to readable duration
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  // Helper: timeframe label for descriptions
  const timeframeLabel =
    {
      day: "Vandaag",
      week: "Afgelopen 7 dagen",
      month: "Afgelopen 30 dagen",
      "90days": "Afgelopen 90 dagen",
      "6months": "Afgelopen 6 maanden",
      year: "Afgelopen jaar",
    }[timeframe] || "Afgelopen 7 dagen";

  // Device icon helper
  const getDeviceIcon = (device) => {
    const d = device.toLowerCase();
    if (d === "desktop") return Monitor;
    if (d === "mobile") return Smartphone;
    if (d === "tablet") return Tablet;
    return Globe;
  };

  // Device color helper
  const deviceColors = [
    isDark ? "#818cf8" : "#4f46e5",
    isDark ? "#34d399" : "#10b981",
    isDark ? "#fbbf24" : "#f59e0b",
  ];
  /* META TEMPORARILY DISABLED
  if (connections.meta.connected) {
    statCards.push({
      label: "Volgers",
      value: (stats.totalFollowers || 0).toLocaleString(),
      icon: UserPlus,
    });
    statCards.push({
      label: "Posts",
      value: (stats.totalPosts || 0).toLocaleString(),
      icon: BarChart3,
    });
    statCards.push({
      label: "Impressies",
      value: (stats.totalImpressions || 0).toLocaleString(),
      icon: Eye,
    });
    if (stats.totalReach > 0)
      statCards.push({
        label: "Bereik",
        value: stats.totalReach.toLocaleString(),
        icon: Share2,
      });
    if (stats.totalEngagement > 0)
      statCards.push({
        label: "Engagement",
        value: stats.totalEngagement.toLocaleString(),
        icon: TrendingUp,
      });
  }
  */
  if (connections.google.connected && stats.googleAnalytics) {
    statCards.push({
      label: "Sessies",
      value: (stats.googleAnalytics.totalVisitors || 0).toLocaleString(),
      icon: Globe,
      dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
      tooltip: "Het totaal aantal bezoeken aan uw website. Eén bezoeker kan meerdere sessies hebben.",
    });
    statCards.push({
      label: "Unieke bezoekers",
      value: (stats.googleAnalytics.uniqueVisitors || 0).toLocaleString(),
      icon: UserPlus,
      dot: isDark ? "bg-indigo-400" : "bg-indigo-500",
      tooltip: "Het aantal unieke personen dat uw website heeft bezocht. Elke bezoeker wordt slechts één keer geteld.",
    });
    statCards.push({
      label: "Paginaweergaven",
      value: (stats.googleAnalytics.totalPageViews || 0).toLocaleString(),
      icon: Eye,
      dot: isDark ? "bg-sky-400" : "bg-sky-500",
      tooltip: "Het totaal aantal pagina's dat is bekeken. Herhaalde weergaven van dezelfde pagina worden ook meegeteld.",
    });
    statCards.push({
      label: "Bouncepercentage",
      value: `${(stats.googleAnalytics.bounceRate || 0).toFixed(1)}%`,
      icon: ArrowDownRight,
      dot: isDark ? "bg-amber-400" : "bg-amber-500",
      tooltip: "Het percentage bezoekers dat uw website verlaat na slechts één pagina te bekijken, zonder verdere interactie. Een lager percentage is beter.",
    });
    statCards.push({
      label: "Gem. sessieduur",
      value: formatDuration(stats.googleAnalytics.avgSessionDuration || 0),
      icon: Clock,
      dot: isDark ? "bg-violet-400" : "bg-violet-500",
      tooltip: "De gemiddelde tijd die een bezoeker op uw website doorbrengt per sessie. Een langere duur wijst op meer betrokkenheid.",
    });
    statCards.push({
      label: "Betrokkenheid",
      value: `${(stats.googleAnalytics.engagementRate || 0).toFixed(1)}%`,
      icon: Zap,
      dot: isDark ? "bg-rose-400" : "bg-rose-500",
      tooltip: "Het percentage sessies waarbij de bezoeker actief betrokken was (langer dan 10 seconden, meerdere pagina's bezocht, of een conversie voltooide). Een hoger percentage is beter.",
    });
  }

  // Are we comparing multiple properties?
  const isComparing =
    viewMode === "comparison" &&
    selectedProperties.length > 1 &&
    stats.propertiesData &&
    Object.keys(stats.propertiesData).length > 1;

  // Comparison metric definitions
  const comparisonMetrics = [
    {
      label: "Sessies",
      icon: Globe,
      getValue: (d) => (d.totalVisitors || 0).toLocaleString(),
      dot: isDark ? "bg-emerald-400" : "bg-emerald-500",
      tooltip: "Het totaal aantal bezoeken aan uw website. Eén bezoeker kan meerdere sessies hebben.",
    },
    {
      label: "Unieke bezoekers",
      icon: UserPlus,
      getValue: (d) => (d.uniqueVisitors || 0).toLocaleString(),
      dot: isDark ? "bg-indigo-400" : "bg-indigo-500",
      tooltip: "Het aantal unieke personen dat uw website heeft bezocht. Elke bezoeker wordt slechts één keer geteld.",
    },
    {
      label: "Paginaweergaven",
      icon: Eye,
      getValue: (d) => (d.totalPageViews || 0).toLocaleString(),
      dot: isDark ? "bg-sky-400" : "bg-sky-500",
      tooltip: "Het totaal aantal pagina's dat is bekeken. Herhaalde weergaven van dezelfde pagina worden ook meegeteld.",
    },
    {
      label: "Bouncepercentage",
      icon: ArrowDownRight,
      getValue: (d) => `${(d.bounceRate || 0).toFixed(1)}%`,
      dot: isDark ? "bg-amber-400" : "bg-amber-500",
      tooltip: "Het percentage bezoekers dat uw website verlaat na slechts één pagina te bekijken, zonder verdere interactie. Een lager percentage is beter.",
    },
    {
      label: "Gem. sessieduur",
      icon: Clock,
      getValue: (d) => formatDuration(d.avgSessionDuration || 0),
      dot: isDark ? "bg-violet-400" : "bg-violet-500",
      tooltip: "De gemiddelde tijd die een bezoeker op uw website doorbrengt per sessie. Een langere duur wijst op meer betrokkenheid.",
    },
    {
      label: "Betrokkenheid",
      icon: Zap,
      getValue: (d) => `${(d.engagementRate || 0).toFixed(1)}%`,
      dot: isDark ? "bg-rose-400" : "bg-rose-500",
      tooltip: "Het percentage sessies waarbij de bezoeker actief betrokken was (langer dan 10 seconden, meerdere pagina's bezocht, of een conversie voltooide). Een hoger percentage is beter.",
    },
  ];

  // Build comparison chart data (merge all properties' visitors by date)
  const comparisonChartData =
    isComparing && stats.propertiesData
      ? (() => {
        const merged = {};
        Object.entries(stats.propertiesData).forEach(([propId, data]) => {
          (data.visitorsByDay || []).forEach((day) => {
            if (!merged[day.date]) merged[day.date] = { date: day.date };
            merged[day.date][`sessions_${propId}`] = day.visitors;
          });
        });
        return Object.values(merged).sort((a, b) =>
          a.date.localeCompare(b.date),
        );
      })()
      : null;

  // Helper: render detail sections (pages, traffic, devices, countries) for a given data set
  const renderPropertyDetails = (gaData) => (
    <>
      {/* Popular pages */}
      {gaData.popularPages && gaData.popularPages.length > 0 && (
        <div
          className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div className="p-4 pb-2">
            <h3
              className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              Populaire pagina's
            </h3>
          </div>
          <div
            className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-100"}`}
          >
            {gaData.popularPages.slice(0, 5).map((page, index) => (
              <div
                key={index}
                className={`px-4 py-3 flex items-center justify-between ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50"}`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`text-[11px] font-semibold w-5 h-5 rounded flex items-center justify-center ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"}`}
                  >
                    {index + 1}
                  </span>
                  <span
                    className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {page.page}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    {page.views} views
                  </span>
                  <span
                    className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    {page.clicks} sessies
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Traffic Sources & Device Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Traffic Sources */}
        {gaData.trafficSources && gaData.trafficSources.length > 0 && (
          <div
            className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="p-4 pb-2">
              <div className="flex items-center gap-2">
                <ArrowUpRight
                  className={`h-3.5 w-3.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}
                />
                <h3
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Verkeersbronnen
                </h3>
              </div>
              <span
                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
              >
                {timeframeLabel}
              </span>
            </div>
            <div
              className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-100"}`}
            >
              {gaData.trafficSources.map((source, index) => {
                const totalSessions = gaData.trafficSources.reduce(
                  (sum, s) => sum + s.sessions,
                  0,
                );
                const percentage =
                  totalSessions > 0
                    ? ((source.sessions / totalSessions) * 100).toFixed(1)
                    : 0;
                return (
                  <div
                    key={index}
                    className={`px-4 py-3 ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50"}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span
                        className={`text-[13px] font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        {source.channel}
                      </span>
                      <div className="flex items-center gap-3">
                        <span
                          className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {source.sessions} sessies
                        </span>
                        <span
                          className={`text-[11px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {percentage}%
                        </span>
                      </div>
                    </div>
                    <div
                      className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                    >
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-indigo-400 transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Device Breakdown */}
        {gaData.deviceBreakdown && gaData.deviceBreakdown.length > 0 && (
          <div
            className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="p-4 pb-2">
              <div className="flex items-center gap-2">
                <Monitor
                  className={`h-3.5 w-3.5 ${isDark ? "text-sky-400" : "text-sky-600"}`}
                />
                <h3
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  Apparaten
                </h3>
              </div>
              <span
                className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
              >
                {timeframeLabel}
              </span>
            </div>
            <div className="p-4 pt-2">
              <div className="space-y-3">
                {gaData.deviceBreakdown.map((device, index) => {
                  const totalDeviceSessions = gaData.deviceBreakdown.reduce(
                    (sum, d) => sum + d.sessions,
                    0,
                  );
                  const percentage =
                    totalDeviceSessions > 0
                      ? ((device.sessions / totalDeviceSessions) * 100).toFixed(
                        1,
                      )
                      : 0;
                  const DeviceIcon = getDeviceIcon(device.device);
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <DeviceIcon
                            className={`h-3.5 w-3.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          />
                          <span
                            className={`text-[13px] font-medium capitalize ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {device.device}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            {device.users} gebruikers
                          </span>
                          <span
                            className={`text-[11px] font-semibold min-w-[42px] text-right ${isDark ? "text-white" : "text-gray-900"}`}
                          >
                            {percentage}%
                          </span>
                        </div>
                      </div>
                      <div
                        className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-white/[0.04]" : "bg-gray-100"}`}
                      >
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor:
                              deviceColors[index % deviceColors.length],
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Country Breakdown */}
      {gaData.countryBreakdown && gaData.countryBreakdown.length > 0 && (
        <div
          className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
        >
          <div className="p-4 pb-2">
            <div className="flex items-center gap-2">
              <MapPin
                className={`h-3.5 w-3.5 ${isDark ? "text-rose-400" : "text-rose-600"}`}
              />
              <h3
                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Landen
              </h3>
            </div>
            <span
              className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
            >
              Top 10 — {timeframeLabel.toLowerCase()}
            </span>
          </div>
          <div
            className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-100"}`}
          >
            {gaData.countryBreakdown.map((item, index) => {
              const totalCountrySessions = gaData.countryBreakdown.reduce(
                (sum, c) => sum + c.sessions,
                0,
              );
              const percentage =
                totalCountrySessions > 0
                  ? ((item.sessions / totalCountrySessions) * 100).toFixed(1)
                  : 0;
              return (
                <div
                  key={index}
                  className={`px-4 py-3 flex items-center justify-between ${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-[11px] font-semibold w-5 h-5 rounded flex items-center justify-center ${isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-500"}`}
                    >
                      {index + 1}
                    </span>
                    <span
                      className={`text-[13px] ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {item.country}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      {item.sessions} sessies
                    </span>
                    <span
                      className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    >
                      {item.users} gebruikers
                    </span>
                    <span
                      className={`text-[11px] font-medium min-w-[40px] text-right ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {percentage}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        {/* Header with title, mode toggle, property selector, filters */}
        <div className="space-y-3">
          {/* Top row: Title + Action buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1
                className={`text-[15px] font-semibold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Statistieken
              </h1>
              {connections.google.connected && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}
                >
                  <div
                    className={`h-1.5 w-1.5 rounded-full ${isDark ? "bg-emerald-400" : "bg-emerald-500"}`}
                  />
                  Verbonden
                </span>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowSetupGuide(!showSetupGuide)}
                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${showSetupGuide ? (isDark ? "text-indigo-400 bg-indigo-500/10" : "text-indigo-600 bg-indigo-50") : isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                title="Setup handleiding"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
              {isAdmin && (
                <button
                  onClick={() => setShowConnections(!showConnections)}
                  className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                >
                  <Settings className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                onClick={fetchStats}
                disabled={loading}
                className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>

          {/* Controls row: Mode toggle + Property selector + Timeframe */}
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center gap-2.5 p-2.5 rounded-xl ${isDark ? "bg-white/[0.02] border border-white/[0.04]" : "bg-gray-50/80 border border-gray-100"}`}
          >
            {/* View mode toggle */}
            {connections.google.connected && gaProperties.length > 1 && (
              <div
                className={`inline-flex rounded-lg p-0.5 ${isDark ? "bg-white/[0.04]" : "bg-white border border-gray-200"}`}
              >
                <button
                  onClick={() => {
                    setViewMode("normal");
                    // Keep only first selected property
                    if (selectedProperties.length > 1) {
                      setSelectedProperties([selectedProperties[0]]);
                    }
                  }}
                  className={`h-7 px-3 rounded-md text-[12px] font-medium transition-all flex items-center gap-1.5 ${viewMode === "normal"
                    ? isDark
                      ? "bg-white/[0.08] text-white shadow-sm"
                      : "bg-white text-gray-900 shadow-sm"
                    : isDark
                      ? "text-gray-500 hover:text-gray-300"
                      : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <LayoutGrid className="h-3 w-3" />
                  Normaal
                </button>
                <button
                  onClick={() => {
                    setViewMode("comparison");
                    // Select all properties for comparison
                    setSelectedProperties(
                      gaProperties.map((p) => p.name.split("/").pop()),
                    );
                  }}
                  className={`h-7 px-3 rounded-md text-[12px] font-medium transition-all flex items-center gap-1.5 ${viewMode === "comparison"
                    ? isDark
                      ? "bg-white/[0.08] text-white shadow-sm"
                      : "bg-white text-gray-900 shadow-sm"
                    : isDark
                      ? "text-gray-500 hover:text-gray-300"
                      : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <GitCompareArrows className="h-3 w-3" />
                  Vergelijken
                </button>
              </div>
            )}

            {/* Divider */}
            {connections.google.connected && gaProperties.length > 1 && (
              <div
                className={`hidden sm:block h-5 w-px ${isDark ? "bg-white/[0.06]" : "bg-gray-200"}`}
              />
            )}

            {/* Property selector */}
            {connections.google.connected &&
              gaProperties.length > 1 &&
              (viewMode === "normal" ? (
                /* Normal mode: dropdown for single property */
                <Select
                  value={selectedProperties[0] || ""}
                  onValueChange={(val) => selectSingleProperty(val)}
                >
                  <SelectTrigger
                    className={`w-[200px] h-8 rounded-lg text-[12px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="h-3 w-3" />
                      <SelectValue placeholder="Property kiezen" />
                    </div>
                  </SelectTrigger>
                  <SelectContent
                    className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
                  >
                    {gaProperties.map((prop) => {
                      const propId = prop.name.split("/").pop();
                      return (
                        <SelectItem key={prop.name} value={propId}>
                          {prop.displayName || prop.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                /* Comparison mode: multi-select chips */
                <div className="flex items-center gap-1.5 flex-wrap">
                  {gaProperties.map((prop) => {
                    const propId = prop.name.split("/").pop();
                    const isSelected = selectedProperties.includes(propId);
                    const color = getPropertyColor(propId);
                    return (
                      <button
                        key={prop.name}
                        onClick={() => toggleProperty(propId)}
                        className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition-all border ${isSelected
                          ? `${color.bg} ${color.border} ${color.text}`
                          : isDark
                            ? "bg-transparent border-white/[0.06] text-gray-600 hover:bg-white/[0.04]"
                            : "bg-white border-gray-200 text-gray-400 hover:bg-gray-50"
                          }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {isSelected && (
                            <div
                              className={`h-1.5 w-1.5 rounded-full ${color.dot}`}
                            />
                          )}
                          {prop.displayName || prop.name}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Timeframe selector */}
            <Select value={timeframe} onValueChange={setTimeframe}>
              <SelectTrigger
                className={`w-[140px] h-8 rounded-lg text-[12px] ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  <SelectValue placeholder="Periode" />
                </div>
              </SelectTrigger>
              <SelectContent
                className={isDark ? "bg-[#1a1a1a] border-white/[0.08]" : ""}
              >
                <SelectItem value="day">Vandaag</SelectItem>
                <SelectItem value="week">7 dagen</SelectItem>
                <SelectItem value="month">30 dagen</SelectItem>
                <SelectItem value="90days">90 dagen</SelectItem>
                <SelectItem value="6months">6 maanden</SelectItem>
                <SelectItem value="year">1 jaar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stat cards */}
        {isComparing ? (
          /* Comparison mode: compact horizontal table */
          <div
            className={`rounded-xl overflow-hidden ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr
                    className={
                      isDark
                        ? "border-b border-white/[0.06]"
                        : "border-b border-gray-100"
                    }
                  >
                    <th
                      className={`px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    >
                      Property
                    </th>
                    {comparisonMetrics.map((metric) => (
                      <th
                        key={metric.label}
                        className={`px-3 py-2.5 text-[11px] font-medium uppercase tracking-wider text-right ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        <RadixTooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-end gap-1 cursor-help">
                              <metric.icon className="h-3 w-3" />
                              {metric.label}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className={`max-w-[250px] text-[12px] font-normal normal-case tracking-normal ${isDark ? "bg-[#1a1a1a] border-white/10 text-gray-300" : ""}`}
                          >
                            {metric.tooltip}
                          </TooltipContent>
                        </RadixTooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDark ? "divide-white/[0.04]" : "divide-gray-50"}`}
                >
                  {selectedProperties.map((propId) => {
                    const data = stats.propertiesData?.[propId];
                    if (!data) return null;
                    const color = getPropertyColor(propId);
                    return (
                      <tr
                        key={propId}
                        className={`${isDark ? "hover:bg-white/[0.02]" : "hover:bg-gray-50/50"} transition-colors`}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-2 w-2 rounded-full flex-shrink-0 ${color.dot}`}
                            />
                            <span
                              className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {getPropertyName(propId)}
                            </span>
                          </div>
                        </td>
                        {comparisonMetrics.map((metric) => (
                          <td
                            key={metric.label}
                            className={`px-3 py-2.5 text-right`}
                          >
                            <span
                              className={`text-[13px] font-semibold tabular-nums ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {metric.getValue(data)}
                            </span>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : statCards.length > 0 ? (
          /* Normal mode: clean stat card grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {statCards.map((s) => {
              const cardContent = (
                <div
                  className={`rounded-xl p-3.5 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <s.icon
                      className={`h-3.5 w-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                    />
                    <span
                      className={`text-[11px] font-medium uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-500"}`}
                    >
                      {s.label}
                    </span>
                  </div>
                  <span
                    className={`text-xl font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {s.value}
                  </span>
                </div>
              );

              return s.tooltip ? (
                <RadixTooltip key={s.label}>
                  <TooltipTrigger asChild>
                    {cardContent}
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className={`max-w-[250px] text-[12px] ${isDark ? "bg-[#1a1a1a] border-white/10 text-gray-300" : ""}`}
                  >
                    {s.tooltip}
                  </TooltipContent>
                </RadixTooltip>
              ) : (
                <div key={s.label}>{cardContent}</div>
              );
            })}
          </div>
        ) : null}

        {/* Show setup guide */}
        {showSetupGuide && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <SetupGuide isDark={isDark} isAdmin={isAdmin} />
          </motion.div>
        )}

        {/* Show connections panel (admin only) */}
        {isAdmin && showConnections && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <SocialMediaConnect
              onConnect={(platform) => {
                setConnections((prev) => ({
                  ...prev,
                  [platform]: { connected: true, data: null },
                }));
              }}
            />
          </motion.div>
        )}

        {/* Facebook Pages - META TEMPORARILY DISABLED */}
        {/* stats.facebookPages && stats.facebookPages.length > 0 && (
        <div className="space-y-3">
          <h2
            className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            <Facebook className="h-4 w-4 text-blue-500" />
            Facebook Pages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.facebookPages.map((page) => (
              <div
                key={page.id}
                className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <h3
                  className={`text-[13px] font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  {page.name}
                </h3>
                <div className="space-y-2">
                  {page.posts > 0 && (
                    <div className="flex justify-between items-center">
                      <span
                        className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Posts
                      </span>
                      <span
                        className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {page.posts}
                      </span>
                    </div>
                  )}
                  {page.impressions > 0 && (
                    <div className="flex justify-between items-center">
                      <span
                        className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                      >
                        Impressies
                      </span>
                      <span
                        className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {page.impressions.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) */}

        {/* Instagram Accounts - META TEMPORARILY DISABLED */}
        {/* stats.instagramAccounts && stats.instagramAccounts.length > 0 && (
        <div className="space-y-3">
          <h2
            className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          >
            <Instagram className="h-4 w-4 text-pink-500" />
            Instagram Accounts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stats.instagramAccounts.map((account) => (
              <div
                key={account.id}
                className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
              >
                <h3
                  className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  @{account.username}
                </h3>
                <span
                  className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                >
                  {account.pageName}
                </span>
                <div className="space-y-2 mt-3">
                  {[
                    { label: "Volgers", value: account.followers },
                    { label: "Posts", value: account.posts },
                    { label: "Impressies", value: account.impressions },
                    { label: "Bereik", value: account.reach },
                    { label: "Engagement", value: account.engagement },
                  ]
                    .filter((item) => item.value > 0)
                    .map((item) => (
                      <div
                        key={item.label}
                        className="flex justify-between items-center"
                      >
                        <span
                          className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          {item.label}
                        </span>
                        <span
                          className={`text-[13px] font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {item.value.toLocaleString()}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) */}

        {/* Google Analytics */}
        {stats.googleAnalytics && (
          <div className="space-y-3">
            <h2
              className={`text-[13px] font-medium flex items-center gap-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}
            >
              <BarChart3 className="h-4 w-4 text-emerald-500" />
              Website Analytics
            </h2>

            {/* Chart */}
            <div
              className={`rounded-xl p-4 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-white border border-gray-200/80"}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3
                    className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {isComparing ? "Sessies vergelijking" : "Bezoekers per dag"}
                  </h3>
                  <span
                    className={`text-[11px] ${isDark ? "text-gray-600" : "text-gray-500"}`}
                  >
                    {timeframeLabel}
                  </span>
                </div>
              </div>

              {isComparing &&
                comparisonChartData &&
                comparisonChartData.length > 0 ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={comparisonChartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={isDark ? "rgba(255,255,255,0.04)" : "#f0f0f0"}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: isDark ? "#666" : "#999", fontSize: 11 }}
                      />
                      <YAxis
                        tick={{ fill: isDark ? "#666" : "#999", fontSize: 11 }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: isDark
                            ? "1px solid rgba(255,255,255,0.06)"
                            : "1px solid #eee",
                          backgroundColor: isDark ? "#1a1a1a" : "#fff",
                          color: isDark ? "#fff" : "#000",
                          fontSize: 13,
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      {selectedProperties.map((propId) => {
                        const color = getPropertyColor(propId);
                        return (
                          <Line
                            key={propId}
                            type="monotone"
                            dataKey={`sessions_${propId}`}
                            stroke={color.main}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                            name={getPropertyName(propId)}
                            connectNulls
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : !isComparing &&
                stats.googleAnalytics.visitorsByDay.length > 0 ? (
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.googleAnalytics.visitorsByDay}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={isDark ? "rgba(255,255,255,0.04)" : "#f0f0f0"}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{
                          fill: isDark ? "#666" : "#999",
                          fontSize: 11,
                        }}
                      />
                      <YAxis
                        tick={{
                          fill: isDark ? "#666" : "#999",
                          fontSize: 11,
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: isDark
                            ? "1px solid rgba(255,255,255,0.06)"
                            : "1px solid #eee",
                          backgroundColor: isDark ? "#1a1a1a" : "#fff",
                          color: isDark ? "#fff" : "#000",
                          fontSize: 13,
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        iconType="circle"
                        iconSize={8}
                      />
                      <Line
                        type="monotone"
                        dataKey="visitors"
                        stroke={isDark ? "#818cf8" : "#4f46e5"}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Sessies"
                      />
                      <Line
                        type="monotone"
                        dataKey="uniqueVisitors"
                        stroke={isDark ? "#34d399" : "#10b981"}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        activeDot={{ r: 5 }}
                        name="Unieke bezoekers"
                      />
                      <Line
                        type="monotone"
                        dataKey="pageViews"
                        stroke={isDark ? "#fbbf24" : "#f59e0b"}
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 4 }}
                        name="Paginaweergaven"
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div
                  className={`h-48 flex flex-col items-center justify-center rounded-lg ${isDark ? "bg-white/[0.02]" : "bg-gray-50"}`}
                >
                  <Globe
                    className={`h-8 w-8 mb-2 ${isDark ? "text-gray-700" : "text-gray-300"}`}
                  />
                  <p
                    className={`text-[13px] font-medium ${isDark ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Nog geen bezoekersdata
                  </p>
                  <p
                    className={`text-[11px] mt-1 max-w-xs text-center ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Het kan tot 24-48 uur duren voordat Google Analytics data
                    begint te verzamelen na het toevoegen van de tracking code.
                  </p>
                </div>
              )}
            </div>

            {/* Detail sections per property or single */}
            {isComparing
              ? selectedProperties.map((propId) => {
                const data = stats.propertiesData?.[propId];
                if (!data) return null;
                const color = getPropertyColor(propId);
                return (
                  <div key={propId} className="space-y-3">
                    <div
                      className={`flex items-center gap-2 pt-2 pb-1 border-t ${isDark ? "border-white/[0.06]" : "border-gray-200/80"}`}
                    >
                      <div
                        className={`h-2.5 w-2.5 rounded-full ${color.dot}`}
                      />
                      <span
                        className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                      >
                        {getPropertyName(propId)}
                      </span>
                    </div>
                    {renderPropertyDetails(data)}
                  </div>
                );
              })
              : renderPropertyDetails(stats.googleAnalytics)}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default Statistics;
